import { useTranslation } from 'react-i18next'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { Grid, MenuItem, Select, TextField } from '@vapor/material'
import { Controller } from 'react-hook-form'
import { FormItem } from 'hic-components'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import useSendMessageTemplateForm, { SendMessageTemplateFormType } from './send-message-template.hook'
import SendMessageTemplateCrud from './send-message-template.crud'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBookmark } from '@fortawesome/pro-regular-svg-icons'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { filterMessageTemplate } from 'src/app/pages/settings/mail-templates/mail-template-list/filter-message-template'

export default function SendMessageTemplate({
  open,
  onClose,
  onSendSuccess,
  guestEmail,
  propertyId,
  tenantId,
  reservationId,
  reservationThread,
}: {
  open: boolean
  onClose: () => void
  onSendSuccess: () => void
  propertyId: string
  tenantId: string
  reservationId: string
  reservationThread?: string
  guestEmail?: string
}) {
  const { t } = useTranslation()
  const showSuccess = useSuccessSnackBar()
  const { loading, messageTemplates, sendMessageTemplate } = SendMessageTemplateCrud({ tenantId })
  const { control, errors, onFormSubmit, setValue, formValue } = useSendMessageTemplateForm({
    guestEmail,
    onSubmit: async (value) => {
      const result = await sendMessageTemplate({
        variables: {
          input: {
            propertyId,
            tenantId,
            reservationId,
            messageTemplateId: value.messageTemplateId,
            sendTo: value.type === 'MAIL' ? value.sendTo : null,
          },
        },
      })
      if (!result.errors) {
        showSuccess(t('pms.send_message_template.success'))
        onSendSuccess()
      }
    },
  })

  return (
    <FormLayout
      appearance={{
        type: 'DIALOG',
        open,
        onClose,
      }}
      title={t('pms.send_message_template.title')}
      toolbar={
        <FormLayoutDefaultToolbar
          cancelButton={{
            clickHandler: onClose,
            disabled: loading,
          }}
          saveButton={{
            clickHandler: onFormSubmit,
            text: t('commons.send'),
            disabled: loading,
            loading,
          }}
        />
      }
    >
      <Grid container spacing={2} width={'500px'}>
        <Grid item xs={12}>
          <Controller<SendMessageTemplateFormType, 'messageTemplateId'>
            name="messageTemplateId"
            control={control}
            render={({ field }) => {
              return (
                <FormItem fullWidth fieldIcon={<FontAwesomeIcon icon={faBookmark} />} fieldLabel={t('pms.send_message_template.default_message')} errorMessage={errors.messageTemplateId?.message}>
                  <Select
                    value={field.value ?? ''}
                    onChange={(event) => {
                      const messageTemplateId = event.target.value
                      const messageTemplateType = messageTemplates.find((messageTemplate) => messageTemplate.id === messageTemplateId)?.type

                      field.onChange(messageTemplateId)
                      if (messageTemplateType) {
                        setValue('type', messageTemplateType)
                      }
                    }}
                  >
                    <MenuItem value=""></MenuItem>
                    {messageTemplates
                      .filter((messageTemplate) => (!reservationThread ? messageTemplate.type !== 'CHAT' : true))
                      .filter((messageTemplate) => filterMessageTemplate(messageTemplate, messageTemplates))
                      .map((messageTemplate) => (
                        <MenuItem key={messageTemplate.id} value={messageTemplate.id}>
                          {messageTemplate.name} ({messageTemplate.type === 'MAIL' ? t('pms.settings.automation.grid.type.email') : t('pms.settings.automation.grid.type.chat')})
                        </MenuItem>
                      ))}
                  </Select>
                </FormItem>
              )
            }}
          />
        </Grid>
        {formValue.type === 'MAIL' && (
          <Grid item xs={12}>
            <Controller<SendMessageTemplateFormType, 'sendTo'>
              name="sendTo"
              control={control}
              render={({ field }) => {
                return (
                  <FormItem fullWidth fieldLabel={t('pms.send_communication_email.to')} errorMessage={errors.sendTo?.message}>
                    <TextField value={field.value ?? ''} onChange={field.onChange} />
                  </FormItem>
                )
              }}
            />
          </Grid>
        )}
      </Grid>
    </FormLayout>
  )
}
